import {
  SharedStateProvider,
  TocQueryState,
  TocSortDirection,
  TocSortField,
  TransitionsOfCareContent as TransitionsOfCareContentShared,
  useSharedContext,
} from "@metriport/shared-internal";
import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getOSSBaseURL } from "../../../api/apiOSS";
import { capture } from "../../../shared/capture";
import { useAppContext } from "../../contexts/app";
import { useIsUserInSandbox } from "../../shared/useIsUserInSandbox";

const tocManagedKeys = [
  "facility",
  "cohort",
  "type",
  "days",
  "coding",
  "status",
  "class",
  "search",
  "encounterId",
  "sortBy",
  "sortDir",
] as const;
const validTypes = new Set(["Admitted", "Discharged"]);
const validDays = new Set(["2", "7", "14", "28"]);
const validCoding = new Set(["cardiac"]);
const validStatuses = new Set(["Not Started", "Attempted", "Completed"]);
const validClasses = new Set([
  "inpatient encounter",
  "ambulatory",
  "emergency",
  "pre-admission",
  "short stay",
]);
const validSortFields = new Set<TocSortField>([
  "admitTime",
  "dischargeTime",
  "patientFirstName",
  "patientLastName",
]);
const validSortDirections = new Set<TocSortDirection>(["asc", "desc"]);

function TransitionsOfCareContent() {
  const { state } = useAppContext();
  const { sharedState } = useSharedContext();
  const { isUserInSandbox } = useIsUserInSandbox(state);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQueryState = useMemo(() => {
    return parseTocQueryState(searchParams);
  }, [searchParams]);

  const getPatientUrl = useMemo(() => {
    return (patientId: string) => {
      const basePath = isUserInSandbox ? `/sandbox/patient` : `/patient`;
      const versionPath = sharedState.hasDashV2Access ? `/v2/${patientId}` : `/${patientId}`;
      return `${basePath}${versionPath}`;
    };
  }, [isUserInSandbox, sharedState.hasDashV2Access]);

  const handleNavigateToPatient = useMemo(() => {
    return (patientId: string) => {
      navigate(getPatientUrl(patientId));
    };
  }, [getPatientUrl, navigate]);

  const handleQueryStateChange = useCallback(
    (queryState: TocQueryState) => {
      const nextSearchParams = createTocSearchParams(searchParams, queryState);

      if (nextSearchParams.toString() === searchParams.toString()) {
        return;
      }

      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return (
    <TransitionsOfCareContentShared
      initialQueryState={initialQueryState}
      onQueryStateChange={handleQueryStateChange}
      onNavigateToPatient={handleNavigateToPatient}
      getPatientUrl={getPatientUrl}
      isImpersonating={state.isImpersonating}
      capture={capture}
    />
  );
}

export default function TransitionsOfCare() {
  const { state } = useAppContext();
  const { isUserInSandbox } = useIsUserInSandbox(state);
  const baseUrl = getOSSBaseURL(isUserInSandbox);
  const isMetriportUser = !!state.user?.email?.endsWith("@metriport.com");

  if (!state.isLoaded) return null;

  return (
    <SharedStateProvider
      authToken={state.authToken}
      baseUrl={baseUrl}
      ehrDetails={undefined}
      isMetriportUser={isMetriportUser}
      isUserInSandbox={isUserInSandbox}
      userEmail={state.user?.email}
      featureFlags={state.featureFlags}
    >
      <TransitionsOfCareContent />
    </SharedStateProvider>
  );
}

function parseTocQueryState(searchParams: URLSearchParams): TocQueryState {
  const queryState: TocQueryState = {
    ...(searchParams.get("facility")
      ? { facility: searchParams.get("facility") ?? undefined }
      : {}),
    ...(searchParams.get("cohort") ? { cohort: searchParams.get("cohort") ?? undefined } : {}),
    ...(searchParams.get("type") && validTypes.has(searchParams.get("type") ?? "")
      ? { type: searchParams.get("type") as TocQueryState["type"] }
      : {}),
    ...(searchParams.get("days") && validDays.has(searchParams.get("days") ?? "")
      ? { days: searchParams.get("days") as TocQueryState["days"] }
      : {}),
    ...(searchParams.get("coding") && validCoding.has(searchParams.get("coding") ?? "")
      ? { coding: searchParams.get("coding") as TocQueryState["coding"] }
      : {}),
    ...(searchParams.get("status") && validStatuses.has(searchParams.get("status") ?? "")
      ? { status: searchParams.get("status") as TocQueryState["status"] }
      : {}),
    ...(searchParams.get("search")?.trim() ? { search: searchParams.get("search")?.trim() } : {}),
    ...(searchParams.get("encounterId")
      ? { encounterId: searchParams.get("encounterId") ?? undefined }
      : {}),
  };
  const classValues = Array.from(
    new Set(searchParams.getAll("class").filter(className => validClasses.has(className)))
  );
  const sortBy = searchParams.get("sortBy");
  const sortDir = searchParams.get("sortDir");

  if (classValues.length > 0) {
    queryState.class = classValues as TocQueryState["class"];
  }
  if (sortBy && validSortFields.has(sortBy as TocSortField)) {
    queryState.sortBy = sortBy as TocSortField;
  }
  if (sortDir && validSortDirections.has(sortDir as TocSortDirection)) {
    queryState.sortDir = sortDir as TocSortDirection;
  }
  if (queryState.sortBy === "dischargeTime" && !queryState.type) {
    queryState.type = "Discharged";
  }
  if (!queryState.sortBy) {
    queryState.sortDir = undefined;
  }
  if (!queryState.sortDir) {
    queryState.sortBy = undefined;
  }

  return queryState;
}

function createTocSearchParams(
  currentSearchParams: URLSearchParams,
  queryState: TocQueryState
): URLSearchParams {
  const nextSearchParams = new URLSearchParams(currentSearchParams);

  tocManagedKeys.forEach(key => nextSearchParams.delete(key));

  if (queryState.facility) nextSearchParams.set("facility", queryState.facility);
  if (queryState.cohort) nextSearchParams.set("cohort", queryState.cohort);
  if (queryState.type) nextSearchParams.set("type", queryState.type);
  if (queryState.days) nextSearchParams.set("days", queryState.days);
  if (queryState.coding) nextSearchParams.set("coding", queryState.coding);
  if (queryState.status) nextSearchParams.set("status", queryState.status);
  if (queryState.search) nextSearchParams.set("search", queryState.search);
  if (queryState.encounterId) nextSearchParams.set("encounterId", queryState.encounterId);
  queryState.class?.forEach(className => nextSearchParams.append("class", className));
  if (queryState.sortBy) nextSearchParams.set("sortBy", queryState.sortBy);
  if (queryState.sortDir) nextSearchParams.set("sortDir", queryState.sortDir);

  return nextSearchParams;
}
